import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MailOpen } from "lucide-react";
import {
  type InviteEntry,
} from "@travelix/core";

import { useAuth } from "@/lib/auth-provider";
import { Button } from "@/components/ui/button";
import { acceptTripInvite, denyTripInvite, unwrap, queryKeys, displayNameOf } from "@/lib/api";

/**
 * A single pending trip invite on the dashboard. Two actions:
 *   - Accept → the trip joins the user's list and shows up like any other.
 *   - Decline → the invite is removed on both sides, no further trace.
 *
 * Both mutations invalidate the user-doc query so the dashboard refreshes.
 *
 * Note: InviteEntry doesn't carry trip start/end dates — the invite was
 * created at trip-send time and dates may have changed since. Full trip
 * detail is fetched when the invite is accepted.
 */
export function InviteCard({ invite }: { invite: InviteEntry }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  // Tracks the outgoing animation on accept/decline before the parent
  // list re-renders without this invite. Fine to keep local — the
  // mutation invalidates the user query and this component unmounts.
  const [leaving, setLeaving] = useState(false);

  // Suppress the city subtitle when it's identical to the trip name — some
  // trips are literally named after their destination ("Japan"/"Japan").
  const showCity =
    !!invite.city &&
    invite.city.trim().toLowerCase() !== invite.tripName.trim().toLowerCase();

  // Invalidation is deferred (not useTripMutation's on-settle) so the card
  // can finish its exit animation before the dashboard list re-renders.
  const leave = () => {
    setLeaving(true);
    setTimeout(() => qc.invalidateQueries({ queryKey: queryKeys.users() }), 320);
  };

  const accept = useMutation({
    mutationFn: async () => {
      if (!user?.uid || !user.email) throw new Error("Not signed in");
      await unwrap(
        acceptTripInvite(invite, { uid: user.uid, email: user.email, name: displayNameOf(user) }),
      );
    },
    onSuccess: () => {
      leave();
      toast.success(`Joined "${invite.tripName}".`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't accept."),
  });

  const decline = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error("Not signed in");
      await unwrap(denyTripInvite(invite, user.email));
    },
    onSuccess: () => {
      leave();
      toast.success("Invite declined.");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't decline."),
  });

  const busy = accept.isPending || decline.isPending;

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={
        leaving ? { opacity: 0, scale: 0.95 } : { opacity: 1, y: 0, scale: 1 }
      }
      transition={{ duration: 0.28, ease: "easeOut" }}
      className="rounded-lg border border-primary/30 bg-primary-soft/40 p-4"
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-ink"
          aria-hidden
        >
          <MailOpen className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-ink-muted m-0">
            <span className="font-semibold text-ink">
              {invite.inviterName || "A friend"}
            </span>{" "}
            invited you to
          </p>
          <h3
            className="font-display text-xl leading-tight tracking-tight text-ink m-0 mt-0.5 truncate"
            style={{ fontVariationSettings: `"opsz" 36, "wght" 500` }}
          >
            {invite.tripName}
          </h3>
          {showCity ? (
            <p className="mt-1 text-[12px] text-ink-subtle m-0 truncate">
              {invite.city}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          onClick={() => accept.mutate()}
          disabled={busy}
          size="sm"
          block
        >
          {accept.isPending ? "Joining…" : "Accept"}
        </Button>
        <Button
          onClick={() => decline.mutate()}
          disabled={busy}
          variant="secondary"
          size="sm"
          block
        >
          {decline.isPending ? "Declining…" : "Decline"}
        </Button>
      </div>
    </motion.article>
  );
}
